// src/components/audit/AuditLogViewer.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme,
  alpha,
  Button
} from '@mui/material';
import {
  ExpandMore,
  Person,
  AccessTime,
  Info,
  Visibility,
  Download,
  AddCircle,
  Edit,
  DeleteForever,
  SwapHoriz,
  GetApp,
  LocalShipping,
  Assignment,
  AssignmentReturn,
  ChangeCircle,
  BatchPrediction,
  Refresh
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { 
  ParsedAuditLog, 
  AuditAction, 
  AuditLogFilters,
  AuditDisplayConfig 
} from '../../types/Audit.types';
import { AuditService } from 'src/services/audit-service';
import StatusCard from '../StatusCard';

interface AuditLogViewerProps {
  // Data source configuration
  entityType?: string;
  entityId?: string;
  apiEndpoint?: string;
  
  // Display configuration
  title?: string;
  config?: Partial<AuditDisplayConfig>;
  filters?: AuditLogFilters;
  
  // Event handlers
  onLogClick?: (log: ParsedAuditLog) => void;
  onError?: (error: string) => void;
  
  // Styling
  maxHeight?: number | string;
  compact?: boolean;

  // Control props to prevent infinite loops
  refreshKey?: number;
  autoRefresh?: boolean;
}

const defaultConfig: AuditDisplayConfig = {
  showUserInfo: true,
  showTimestamp: true,
  showMetadata: true,
  showChanges: true,
  groupByDate: false,
  pageSize: 20,
  allowExport: true
};

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({
  entityType,
  entityId,
  apiEndpoint,
  title = 'Activity History',
  config = {},
  filters = {},
  onLogClick,
  onError,
  maxHeight = 600,
  compact = false,
  refreshKey = 0,
  autoRefresh = false
}) => {
  const theme = useTheme();
  const finalConfig = { ...defaultConfig, ...config };
  
  const [logs, setLogs] = useState<ParsedAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [lastRefreshKey, setLastRefreshKey] = useState(refreshKey);

  // Use refs to track if component is mounted and prevent stale closure issues
  const isMountedRef = useRef(true);
  const loadingRequestRef = useRef<AbortController | null>(null);

  // Icon mapping
  const getActionIcon = (action: AuditAction) => {
    const iconMap: Record<AuditAction, React.ReactElement> = {
      [AuditAction.Create]: <AddCircle />,
      [AuditAction.Update]: <Edit />,
      [AuditAction.Delete]: <DeleteForever />,
      [AuditAction.Move]: <SwapHoriz />,
      [AuditAction.Receive]: <GetApp />,
      [AuditAction.Ship]: <LocalShipping />,
      [AuditAction.Allocate]: <Assignment />,
      [AuditAction.Deallocate]: <AssignmentReturn />,
      [AuditAction.StatusChange]: <ChangeCircle />,
      [AuditAction.BulkUpdate]: <BatchPrediction />
    };
    return iconMap[action] || <Info />;
  };

  const loadAuditLogs = useCallback(async (forceRefresh = false) => {
    // Cancel any pending request
    if (loadingRequestRef.current) {
      loadingRequestRef.current.abort();
    }

    // Create new abort controller
    loadingRequestRef.current = new AbortController();

    setLoading(true);
    setError(null);
    
    try {
      let auditLogs: ParsedAuditLog[];
      
      if (apiEndpoint) {
        // Custom API endpoint
        const response = await fetch(apiEndpoint, {
          signal: loadingRequestRef.current.signal
        });
        if (!response.ok) throw new Error('Failed to fetch audit logs');
        const rawLogs = await response.json();
        auditLogs = rawLogs.map((log: any) => AuditService.parseAuditLog(log));
      } else if (entityType && entityId) {
        // Entity-specific logs with caching control
        auditLogs = await AuditService.getEntityAuditLogs(
          entityType, 
          entityId, 
          page, 
          finalConfig.pageSize,
          !forceRefresh // use cache unless force refresh
        );
      } else {
        // General audit logs with filters
        const response = await AuditService.getAuditLogs(
          filters, 
          page, 
          finalConfig.pageSize,
          !forceRefresh // use cache unless force refresh
        );
        auditLogs = response.items as unknown as ParsedAuditLog[];
        setTotalCount(response.totalCount);
      }
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLogs(auditLogs);
      }
    } catch (err) {
      // Don't show error if request was aborted (component unmounted)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      if (isMountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load audit logs';
        setError(errorMessage);
        onError?.(errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      loadingRequestRef.current = null;
    }
  }, [entityType, entityId, apiEndpoint, filters, page, finalConfig.pageSize, onError]);

  // Effect for initial load and dependency changes
  useEffect(() => {
    loadAuditLogs();
  }, [entityType, entityId, page]); // Removed loadAuditLogs from deps to prevent infinite loop

  // Effect for refresh key changes
  useEffect(() => {
    if (refreshKey !== lastRefreshKey) {
      setLastRefreshKey(refreshKey);
      loadAuditLogs(true); // Force refresh when refresh key changes
    }
  }, [refreshKey, lastRefreshKey, loadAuditLogs]);

  // Effect for filters changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        loadAuditLogs();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters]); // Only filters in deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingRequestRef.current) {
        loadingRequestRef.current.abort();
      }
    };
  }, []);

  const handleExpandLog = useCallback((logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  }, []);

  const handleExport = useCallback(async () => {
    try {
      const blob = await AuditService.exportAuditLogs(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export audit logs');
    }
  }, [filters]);

  const handleRefresh = useCallback(() => {
    loadAuditLogs(true);
  }, [loadAuditLogs]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  }, []);

  const renderLogMetadata = useCallback((log: ParsedAuditLog) => {
    if (!finalConfig.showMetadata || !log.metadata) return null;

    const { InfoData, RelationData, TriggerType } = log.metadata;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Additional Information
        </Typography>
        <Stack spacing={1.5}>
          {TriggerType && (
            <Chip 
              label={`Trigger: ${TriggerType}`} 
              size="small" 
              variant="outlined" 
              color="info"
            />
          )}
          
          {InfoData && Object.keys(InfoData).length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Info Data:
              </Typography>
              {Object.entries(InfoData).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {key}:
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'right', maxWidth: '60%' }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {RelationData && Object.keys(RelationData).length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Related Entities:
              </Typography>
              {Object.entries(RelationData).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {key}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Stack>
      </Box>
    );
  }, [finalConfig.showMetadata]);

  const renderChanges = useCallback((log: ParsedAuditLog) => {
    if (!finalConfig.showChanges || !log.oldValues || !log.newValues) return null;

    const changes: Array<{ field: string; oldValue: any; newValue: any }> = [];
    const allFields = new Set([
      ...Object.keys(log.oldValues || {}),
      ...Object.keys(log.newValues || {})
    ]);

    allFields.forEach(field => {
      const oldValue = log.oldValues?.[field];
      const newValue = log.newValues?.[field];
      
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes.push({ field, oldValue, newValue });
      }
    });

    if (changes.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Changes
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableBody>
              {changes.map(({ field, oldValue, newValue }) => (
                <TableRow key={field}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', minWidth: 120, verticalAlign: 'top' }}>
                    {field}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={{ 
                      bgcolor: alpha(theme.palette.error.main, 0.1), 
                      p: 1, 
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      wordBreak: 'break-word'
                    }}>
                      {oldValue === null || oldValue === undefined ? '(empty)' : String(oldValue)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', color: 'text.secondary', verticalAlign: 'middle' }}>
                    →
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      p: 1, 
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      wordBreak: 'break-word'
                    }}>
                      {newValue === null || newValue === undefined ? '(empty)' : String(newValue)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }, [finalConfig.showChanges, theme.palette.error.main, theme.palette.success.main]);

  const renderLogItem = useCallback((log: ParsedAuditLog) => {
    const isExpanded = expandedLogs.has(log.id);
    const actionColor = AuditService.getActionColor(log.action);

    return (
      <Accordion 
        key={log.id}
        expanded={isExpanded}
        onChange={() => handleExpandLog(log.id)}
        sx={{ 
          '&:before': { display: 'none' },
          boxShadow: 1,
          mb: 1,
          borderRadius: 1,
          '&:first-of-type': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
          '&:last-of-type': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore />}
          sx={{ 
            '& .MuiAccordionSummary-content': { 
              alignItems: 'center',
              gap: 2,
              py: 1
            },
            minHeight: 64
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Chip
              icon={getActionIcon(log.action)}
              label={AuditAction[log.action]}
              color={actionColor}
              size="small"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                {log.description}
              </Typography>
              
              {finalConfig.showUserInfo && (
                <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {log.userName}
                    </Typography>
                  </Box>
                  
                  {finalConfig.showTimestamp && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(log.parsedTimestamp, { addSuffix: true })}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            {onLogClick && (
              <Tooltip title="View Details">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onLogClick(log);
                  }}
                  sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                  }}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              <strong>Entity:</strong> {log.entityType} ({log.entityId})
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              <strong>Timestamp:</strong> {log.parsedTimestamp.toLocaleString()}
            </Typography>

            {renderChanges(log)}
            {renderLogMetadata(log)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }, [expandedLogs, handleExpandLog, getActionIcon, finalConfig.showUserInfo, finalConfig.showTimestamp, onLogClick, theme.palette.info.main, renderChanges, renderLogMetadata]);

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Loading audit logs...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleRefresh}
            startIcon={<Refresh />}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title={title}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            {finalConfig.allowExport && (
              <Tooltip title="Export Logs">
                <IconButton onClick={handleExport}>
                  <Download />
                </IconButton>
              </Tooltip>
            )}
            <Chip 
              label={`${logs.length} logs`} 
              size="small" 
              variant="outlined" 
              color="primary"
            />
          </Stack>
        }
        sx={{ pb: 1 }}
      />
      
      <CardContent sx={{ pt: 0 }}>
        {logs.length === 0 ? (
          <StatusCard
            variant="empty"
            title="No audit logs found"
            message="No audit logs match your current filters. Try adjusting your search criteria."
            layout="card"
          />
        ) : (
          <Box sx={{ maxHeight, overflow: 'auto' }}>
            <Stack spacing={0}>
              {logs.map(log => renderLogItem(log))}
            </Stack>
            
            {totalCount > finalConfig.pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Pagination
                  count={Math.ceil(totalCount / finalConfig.pageSize)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AuditLogViewer;